import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { t } from '@/i18n';
import {
  ArrowLeft,
  CircleCheck,
  Download,
  Info,
  Loader2,
  Merge,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DatePicker } from '@/components/shared/DatePicker';
import { useInvoicesApi, useCompaniesApi, useContractorsApi, useExportsApi, MBankPaymentRecord } from '@/services/api';
import type { InvoiceDetail, InvoiceListItem, CompanyDetail, CompanyBankAccount, PaymentType, ChargeBearerCode, ReimbursementExportRecord, ContractorListItem, ContractorBankAccountData } from '@ip/shared-types';
import { cleanIban, detectPaymentTypeFromAccount, isSepaCountry, validateSwiftBic } from '@/utils/iban';
import { EXPORT_ISSUE_LABELS } from '@/utils/exportIssueLabels';

const ALERT_INFO_CLASS = 'border-sky-500/50 text-sky-800 dark:text-sky-300 [&>svg]:text-sky-600 dark:[&>svg]:text-sky-400';
const ALERT_WARNING_CLASS = 'border-amber-500/50 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400';

const COLUMN_COUNT = 12;

interface MBankExportViewProps {
  companyNip: string;
  companyName: string;
  selectedInvoices: InvoiceListItem[];
  onClose: () => void;
}

interface PaymentRecord {
  id: string;
  isMerged: boolean;
  mergedInvoiceIds: string[];
  contractorName: string | null;
  contractorNip: string | null;
  contractorCity: string | null;
  contractorCountry: string | null;
  // Street + postal code travel with the payment: cross-border PAIN blocks
  // want the full beneficiary address (FATF R.16 / EU 2015/847).
  contractorStreet: string | null;
  contractorPostalCode: string | null;
  invoiceNumber: string;
  bankAccount: string | null;
  paymentDate: string | null;
  amount: number;
  netAmount: number | null;
  currency: string | null;
  hasSplitPayment: boolean;
  vatAmount: number | null;
  exportIssues: string[];
  paymentType: PaymentType;
  swiftBic: string | null;
  chargeBearerCode: ChargeBearerCode;
  vatAccountAssigned: boolean | null;
}


const PAYMENT_TYPE_BADGE_CLASSES: Record<PaymentType, string> = {
  domestic: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  sepa: 'border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-300',
  swift: 'border-orange-500/40 bg-orange-500/10 text-orange-700 dark:text-orange-300',
};

const PAYMENT_TYPE_LABELS: Record<PaymentType, string> = {
  domestic: 'DOM',
  sepa: 'SEPA',
  swift: 'SWIFT',
};

// Flat map: cleaned account number → swiftBic (works for contractors with or without NIP)
type BicMap = Map<string, string>;

/**
 * Country as stored is whatever the document printed — "Canada", "Polska",
 * "CA". Only a bare alpha-2 value can be compared against the SEPA list;
 * a full name is deliberately NOT guessed here (the backend owns the
 * name → ISO mapping, `country.util.ts`).
 */
function isoCountryOrNull(country: string | null): string | null {
  const trimmed = (country || '').trim();
  return /^[A-Za-z]{2}$/.test(trimmed) ? trimmed.toUpperCase() : null;
}

/**
 * Payment type for one row. Uses `detectPaymentTypeFromAccount` (the single
 * source of truth shared with InvoiceDetail) whenever the account tells us
 * enough; otherwise the currency + creditor country decide.
 *
 * An account-less foreign invoice must NEVER fall back to 'domestic': that
 * hid the SWIFT/BIC and charge-bearer cells and left the MPP switch live on
 * Canadian EUR invoices (FSS #715), so the operator could not enter the very
 * data the export was blocked on.
 */
function resolvePaymentType(
  bankAccount: string | null,
  currency: string | null,
  country: string | null,
): PaymentType {
  const cur = (currency || 'PLN').toUpperCase();
  const raw = (country || '').trim();
  const iso = isoCountryOrNull(raw);
  // Empty country = assume Poland (the overwhelming majority of rows);
  // anything else that is not "PL" counts as foreign, name or code alike.
  const isForeign = raw !== '' && iso !== 'PL';

  if (bankAccount) {
    const { type } = detectPaymentTypeFromAccount(bankAccount, cur, isForeign ? raw : 'PL');
    // null = non-IBAN account we cannot classify → fall through to the
    // currency/country rules below instead of pretending it is domestic.
    if (type) return type;
  }

  if (!isForeign && cur === 'PLN') return 'domestic';
  if (cur === 'EUR' && iso && isSepaCountry(iso)) return 'sepa';
  // SWIFT is the conservative fallback: it demands a BIC, so nothing slips
  // into the file half-described.
  return 'swift';
}

/**
 * The BIC printed on the invoice wins over the contractor card.
 *
 * The card is a reusable master maintained by an operator; `invoiceSwiftBic` is
 * what THIS document said, captured by OCR/AI at ingest. Consulting only the
 * card is what left the S & S rows with an empty BIC cell despite Azure having
 * read "NOSCCATT" off the page — and an account-less invoice has no map key at
 * all, so the card could not answer even in principle.
 */
function resolveBic(
  bankAccount: string | null,
  bicMap: BicMap,
  invoiceSwiftBic?: string | null,
): string | null {
  const fromInvoice = (invoiceSwiftBic || '').trim().toUpperCase();
  if (fromInvoice) return fromInvoice;
  if (!bankAccount) return null;
  const cleaned = bankAccount.replace(/\s/g, '').toUpperCase();
  return bicMap.get(cleaned) || null;
}

function buildPaymentFromInvoice(
  inv: InvoiceDetail,
  exportIssuesMap: Map<string, string[]>,
  bicMap: BicMap,
): PaymentRecord {
  const paymentType = resolvePaymentType(inv.bankAccount, inv.currency, inv.vendorCountry);
  return {
    id: inv.id,
    isMerged: false,
    mergedInvoiceIds: [inv.id],
    contractorName: inv.contractorName,
    contractorNip: inv.contractorNip,
    contractorCity: inv.vendorCity,
    contractorCountry: inv.vendorCountry,
    contractorStreet: inv.vendorStreet,
    contractorPostalCode: inv.vendorPostalCode,
    invoiceNumber: inv.invoiceNumber || '-',
    bankAccount: inv.bankAccount,
    paymentDate: inv.paymentDate,
    amount: inv.amount || 0,
    netAmount: inv.netAmount ?? null,
    currency: inv.currency,
    hasSplitPayment: paymentType === 'domestic' ? (inv.hasSplitPayment === true) : false,
    vatAmount: inv.taxAmount,
    exportIssues: exportIssuesMap.get(inv.id) || [],
    paymentType,
    swiftBic: resolveBic(inv.bankAccount, bicMap, inv.swiftBic),
    chargeBearerCode: 'SHAR',
    vatAccountAssigned: inv.vatPayerData?.accountAssigned ?? null,
  };
}

export function MBankExportView({
  companyNip,
  companyName,
  selectedInvoices,
  onClose,
}: MBankExportViewProps) {
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceDetail[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [isMerged, setIsMerged] = useState(false);
  const [company, setCompany] = useState<CompanyDetail | null>(null);
  // Default: pay everything today. Overdue invoices carry past due dates that
  // mBank silently shifts/rejects, so the due-date passthrough is opt-out.
  const [executionDate, setExecutionDate] = useState<Dayjs>(dayjs());
  const [overrideDate, setOverrideDate] = useState(true);
  const [confirmReExportOpen, setConfirmReExportOpen] = useState(false);
  // Set when the BACKEND refused the export because an invoice is already
  // paid but the local list snapshot didn't know it (status changed after the
  // invoice list was loaded). Lets the operator confirm from the error
  // instead of dead-ending and losing their in-view edits.
  const [serverPaidWarning, setServerPaidWarning] = useState<string | null>(null);

  const [contractorBicMap, setContractorBicMap] = useState<BicMap>(new Map());
  // Kept so a BIC typed in the table can be written back onto the matching
  // contractor account after the export (see persistTypedBics).
  const [contractors, setContractors] = useState<ContractorListItem[]>([]);

  // Approved employee reimbursements (zwroty) — payable in the same PAIN
  // file; beneficiary = the employee, invoice status untouched server-side.
  const [reimbRecords, setReimbRecords] = useState<ReimbursementExportRecord[]>([]);
  const [selectedReimb, setSelectedReimb] = useState<Set<string>>(new Set());

  const { getInvoice } = useInvoicesApi();
  const { getCompanyDetail } = useCompaniesApi();
  const { getContractors, updateContractor } = useContractorsApi();
  const { generateMBankExport, getReimbursementExportRecords } = useExportsApi();

  const exportIssuesMap = useMemo(
    () => new Map(selectedInvoices.map(inv => [inv.id, inv.exportIssues || []])),
    [selectedInvoices],
  );

  // Invoices in the current payment list that were already sent to the bank
  // ('exported') or already settled ('paid'). Shown as info/warning and
  // re-confirmed before generating the file — re-export is legitimate (e.g. a
  // payment file the bank rejected) but must never happen unnoticed.
  const { alreadyExported, alreadyPaid } = useMemo(() => {
    const byId = new Map(selectedInvoices.map(inv => [inv.id, inv]));
    const includedIds = new Set(payments.flatMap(p => p.mergedInvoiceIds));
    const alreadyExported: InvoiceListItem[] = [];
    const alreadyPaid: InvoiceListItem[] = [];
    for (const id of includedIds) {
      const inv = byId.get(id);
      if (!inv) continue;
      if (inv.status === 'exported') alreadyExported.push(inv);
      else if (inv.status === 'paid') alreadyPaid.push(inv);
    }
    return { alreadyExported, alreadyPaid };
  }, [selectedInvoices, payments]);

  // Determine which currencies are needed and find matching debtor accounts
  const currencyAccountMap = useMemo(() => {
    const map = new Map<string, CompanyBankAccount | null>();
    for (const p of payments) {
      const cur = (p.currency || 'PLN').toUpperCase();
      if (!map.has(cur)) {
        const match = company?.bankAccounts?.find(a => a.currency.toUpperCase() === cur) || null;
        map.set(cur, match);
      }
    }
    // Reimbursements are always paid in PLN.
    if (selectedReimb.size > 0 && !map.has('PLN')) {
      map.set('PLN', company?.bankAccounts?.find(a => a.currency.toUpperCase() === 'PLN') || null);
    }
    return map;
  }, [payments, company, selectedReimb]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [companyData, details, contractors, reimbursements] = await Promise.all([
        getCompanyDetail(companyNip),
        Promise.all(selectedInvoices.map((inv) => getInvoice(companyNip, inv.id))),
        getContractors(companyNip),
        // NOT pre-caught to []: a failure here would silently drop approved
        // employee reimbursements from the payment batch. Let it reach the
        // outer catch so the operator sees the error instead of paying only
        // the invoices.
        getReimbursementExportRecords(companyNip),
      ]);
      setCompany(companyData);
      setInvoiceDetails(details);
      setContractors(contractors);
      setReimbRecords(reimbursements);
      setSelectedReimb(new Set(reimbursements.map((r) => r.reimbursementId)));

      // Build flat BIC lookup: cleanedAccountNumber -> swiftBic
      // Flat map handles contractors with or without NIP (e.g. foreign companies)
      const bicMap: BicMap = new Map();
      for (const c of contractors) {
        if (!c.structuredBankAccounts?.length) continue;
        for (const ba of c.structuredBankAccounts) {
          if (ba.swiftBic) {
            bicMap.set(ba.iban.replace(/\s/g, '').toUpperCase(), ba.swiftBic);
          }
        }
      }
      setContractorBicMap(bicMap);

      const initialPayments: PaymentRecord[] = details.map((inv) =>
        buildPaymentFromInvoice(inv, exportIssuesMap, bicMap)
      );
      setPayments(initialPayments);
    } catch (error) {
      toast.error(t('export.mbank.loadFailed'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [companyNip, selectedInvoices, getInvoice, getCompanyDetail, getContractors, exportIssuesMap]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleMppToggle = useCallback((paymentId: string, checked: boolean) => {
    setPayments((prev) =>
      prev.map((p) => {
        if (p.id !== paymentId) return p;
        // Only allow MPP for domestic
        if (p.paymentType !== 'domestic') return p;
        return { ...p, hasSplitPayment: checked };
      })
    );
  }, []);

  const handleVatAmountChange = useCallback((paymentId: string, value: number | null) => {
    setPayments((prev) =>
      prev.map((p) =>
        p.id === paymentId ? { ...p, vatAmount: value } : p
      )
    );
  }, []);

  const handleSwiftBicChange = useCallback((paymentId: string, value: string) => {
    setPayments((prev) =>
      prev.map((p) =>
        p.id === paymentId ? { ...p, swiftBic: value.toUpperCase().trim() || null } : p
      )
    );
  }, []);

  const handleChargeBearerChange = useCallback((paymentId: string, value: ChargeBearerCode) => {
    setPayments((prev) =>
      prev.map((p) =>
        p.id === paymentId ? { ...p, chargeBearerCode: value } : p
      )
    );
  }, []);

  const handleRemovePayment = useCallback((paymentId: string) => {
    setPayments((prev) => prev.filter((p) => p.id !== paymentId));
  }, []);

  const handleMergePayments = useCallback(() => {
    const groups = new Map<string, PaymentRecord[]>();
    const nonMergeablePayments: PaymentRecord[] = [];

    payments.forEach((payment) => {
      if (payment.hasSplitPayment) {
        nonMergeablePayments.push(payment);
        return;
      }
      if (!payment.bankAccount) {
        nonMergeablePayments.push(payment);
        return;
      }

      // Group by bankAccount + currency + paymentType to prevent mixing
      const key = `${payment.bankAccount}::${(payment.currency || 'PLN').toUpperCase()}::${payment.paymentType}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(payment);
    });

    const mergedPayments: PaymentRecord[] = [];
    groups.forEach((groupPayments) => {
      if (groupPayments.length === 1) {
        mergedPayments.push(groupPayments[0]);
      } else {
        const allInvoiceIds = groupPayments.flatMap((p) => p.mergedInvoiceIds);
        const invoiceNumbers = groupPayments.map((p) => p.invoiceNumber).join(',');
        const totalAmount = groupPayments.reduce((sum, p) => sum + p.amount, 0);
        const totalNet = groupPayments.reduce((sum, p) => sum + (p.netAmount || 0), 0);
        const totalVat = groupPayments.reduce((sum, p) => sum + (p.vatAmount || 0), 0);
        const dates = groupPayments.map((p) => p.paymentDate).filter((d): d is string => d !== null).sort();
        const allIssues = [...new Set(groupPayments.flatMap((p) => p.exportIssues))];
        const first = groupPayments[0];

        mergedPayments.push({
          id: `merged-${first.bankAccount}-${first.currency}`,
          isMerged: true,
          mergedInvoiceIds: allInvoiceIds,
          contractorName: first.contractorName,
          contractorNip: first.contractorNip,
          contractorCity: first.contractorCity,
          contractorCountry: first.contractorCountry,
          contractorStreet: first.contractorStreet,
          contractorPostalCode: first.contractorPostalCode,
          invoiceNumber: invoiceNumbers,
          bankAccount: first.bankAccount,
          paymentDate: dates[0] || null,
          amount: totalAmount,
          netAmount: totalNet || null,
          currency: first.currency,
          hasSplitPayment: false,
          vatAmount: totalVat || null,
          exportIssues: allIssues,
          paymentType: first.paymentType,
          swiftBic: first.swiftBic,
          chargeBearerCode: first.chargeBearerCode,
          // An explicit NIE anywhere in the merged group must stay visible;
          // otherwise unverified (null) wins over a positive answer.
          vatAccountAssigned: groupPayments.some((g) => g.vatAccountAssigned === false)
            ? false
            : groupPayments.every((g) => g.vatAccountAssigned === true)
              ? true
              : null,
        });
      }
    });

    setPayments([...mergedPayments, ...nonMergeablePayments]);
    setIsMerged(true);
    toast.success(t('export.mbank.merged', { regular: mergedPayments.length, unchanged: nonMergeablePayments.length }));
  }, [payments]);

  const handleUnmerge = useCallback(() => {
    // Preserve any manually-entered or auto-resolved BICs — and the charge
    // bearer picked for them: rebuilding from the invoice would silently
    // reset a SWIFT row back to SHAR and the operator would never notice.
    const bicByInvoiceId = new Map<string, string | null>();
    const chargeBearerByInvoiceId = new Map<string, ChargeBearerCode>();
    for (const p of payments) {
      for (const id of p.mergedInvoiceIds) {
        bicByInvoiceId.set(id, p.swiftBic);
        chargeBearerByInvoiceId.set(id, p.chargeBearerCode);
      }
    }
    const originalPayments: PaymentRecord[] = invoiceDetails.map((inv) => {
      const payment = buildPaymentFromInvoice(inv, exportIssuesMap, contractorBicMap);
      const savedBic = bicByInvoiceId.get(inv.id);
      if (savedBic) payment.swiftBic = savedBic;
      const savedChargeBearer = chargeBearerByInvoiceId.get(inv.id);
      if (savedChargeBearer) payment.chargeBearerCode = savedChargeBearer;
      return payment;
    });
    const currentIds = new Set(payments.flatMap((p) => p.mergedInvoiceIds));
    setPayments(originalPayments.filter((p) => currentIds.has(p.id)));
    setIsMerged(false);
    toast.info(t('export.mbank.unmerged'));
  }, [invoiceDetails, payments, exportIssuesMap, contractorBicMap]);

  /**
   * Write BICs typed in the table back onto the contractor's bank account so
   * the next export resolves them automatically (production has a BIC on only
   * a handful of contractor accounts — every foreign transfer used to require
   * retyping it). Matching is by cleaned account number, which also covers
   * contractors without a NIP (foreign vendors).
   *
   * Strictly best-effort: the XML is already downloaded at this point, so a
   * failure here is logged and swallowed — it must never look like the export
   * failed, and there is nothing to undo.
   */
  const persistTypedBics = useCallback(async (exported: PaymentRecord[]) => {
    try {
      const updatesByContractor = new Map<string, ContractorBankAccountData[]>();

      for (const p of exported) {
        if (!p.bankAccount || !p.swiftBic) continue;
        const bic = p.swiftBic.toUpperCase();
        if (!validateSwiftBic(bic).valid) continue;
        const cleanedAccount = cleanIban(p.bankAccount);

        for (const c of contractors) {
          // Start from a pending edit of this contractor when one exists —
          // the account list is replace-all, so two payments hitting the same
          // contractor must accumulate, not overwrite each other.
          const accounts = updatesByContractor.get(c.id) ?? c.structuredBankAccounts ?? [];
          const idx = accounts.findIndex((a) => cleanIban(a.iban) === cleanedAccount);
          if (idx === -1) continue;
          // Only write when the value actually differs from what is stored.
          if ((accounts[idx].swiftBic || '').toUpperCase() === bic) break;
          updatesByContractor.set(
            c.id,
            accounts.map((a, i) => (i === idx ? { ...a, swiftBic: bic } : a)),
          );
          break;
        }
      }

      if (updatesByContractor.size === 0) return;

      for (const [contractorId, structuredBankAccounts] of updatesByContractor) {
        await updateContractor(companyNip, contractorId, { structuredBankAccounts });
      }

      setContractors((prev) =>
        prev.map((c) =>
          updatesByContractor.has(c.id)
            ? { ...c, structuredBankAccounts: updatesByContractor.get(c.id)! }
            : c,
        ),
      );
      setContractorBicMap((prev) => {
        const next = new Map(prev);
        for (const accounts of updatesByContractor.values()) {
          for (const a of accounts) {
            if (a.swiftBic) next.set(cleanIban(a.iban), a.swiftBic);
          }
        }
        return next;
      });
    } catch (error) {
      console.warn('Failed to persist SWIFT/BIC on contractor accounts:', error);
    }
  }, [contractors, companyNip, updateContractor]);

  const performExport = useCallback(async (allowAlreadyPaid: boolean) => {
    // Collect debtor accounts for all currencies present
    const debtorAccounts: CompanyBankAccount[] = [];
    for (const [cur, acct] of currencyAccountMap) {
      if (!acct) {
        toast.error(t('export.mbank.noAccountForCurrency', { currency: cur }));
        return;
      }
      debtorAccounts.push(acct);
    }

    try {
      setExporting(true);

      const exportPayments: MBankPaymentRecord[] = payments.map(p => ({
        id: p.id,
        isMerged: p.isMerged,
        mergedInvoiceIds: p.mergedInvoiceIds,
        contractorName: p.contractorName,
        contractorNip: p.contractorNip,
        contractorCity: p.contractorCity,
        contractorCountry: p.contractorCountry,
        contractorStreet: p.contractorStreet,
        contractorPostalCode: p.contractorPostalCode,
        invoiceNumber: p.invoiceNumber,
        bankAccount: p.bankAccount,
        paymentDate: p.paymentDate,
        amount: p.amount,
        currency: p.currency,
        hasSplitPayment: p.hasSplitPayment,
        vatAmount: p.vatAmount,
        swiftBic: p.swiftBic,
        chargeBearerCode: p.paymentType === 'swift' ? p.chargeBearerCode : null,
        vatAccountAssigned: p.vatAccountAssigned,
      }));

      // Employee reimbursements ride in the same file as plain domestic PLN
      // transfers: beneficiary = employee, title = "Zwrot za ..." (the PAIN
      // remittance comes from invoiceNumber), reimbursementId keeps the
      // backend from flipping any invoice status.
      const reimbPayments: MBankPaymentRecord[] = reimbRecords
        .filter((r) => selectedReimb.has(r.reimbursementId))
        .map((r) => ({
          id: r.reimbursementId,
          reimbursementId: r.reimbursementId,
          isMerged: false,
          mergedInvoiceIds: [],
          contractorName: r.employeeName,
          contractorNip: null,
          contractorCity: r.employeeCity,
          contractorCountry: 'PL',
          invoiceNumber: r.title,
          bankAccount: r.iban,
          paymentDate: null,
          amount: r.amountPln,
          currency: 'PLN',
          hasSplitPayment: false,
        }));

      const blob = await generateMBankExport(companyNip, {
        debtorAccounts,
        executionDate: overrideDate ? executionDate.format('YYYY-MM-DD') : null,
        payments: [...exportPayments, ...reimbPayments],
        allowAlreadyPaid,
      });

      const dateStr = overrideDate ? executionDate.format('YYYYMMDD') : dayjs().format('YYYYMMDD');
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mbank_${companyNip}_${dateStr}.xml`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setServerPaidWarning(null);
      // Exported reimbursements are gone from the approved pool server-side.
      setReimbRecords((prev) => prev.filter((r) => !selectedReimb.has(r.reimbursementId)));
      setSelectedReimb(new Set());
      toast.success(t('export.mbank.generated'));
      // Fire-and-forget: the file is already in the operator's hands.
      void persistTypedBics(payments);
    } catch (error) {
      console.error('Export error:', error);
      const message = error instanceof Error ? error.message : t('export.mbank.generateFailed');
      // The invoice-list snapshot can be stale: an invoice may have become
      // 'paid' after the list was loaded, so the local pre-check didn't ask
      // for confirmation. Ask now, based on the server's verdict.
      if (!allowAlreadyPaid && /already marked as paid/i.test(message)) {
        setServerPaidWarning(message);
        setConfirmReExportOpen(true);
      } else {
        toast.error(message);
      }
    } finally {
      setExporting(false);
    }
  }, [currencyAccountMap, payments, companyNip, executionDate, overrideDate, generateMBankExport, reimbRecords, selectedReimb, persistTypedBics]);

  const handleExport = useCallback(() => {
    // State is initialized at mount — a view left open across midnight would
    // otherwise silently export with yesterday's execution date.
    if (overrideDate && executionDate.isBefore(dayjs().startOf('day'))) {
      toast.error(t('export.mbank.executionDatePast'));
      return;
    }

    const invalidPayments = payments.filter(p => !p.bankAccount || !p.contractorName);
    if (invalidPayments.length > 0) {
      toast.error(t('export.mbank.missingAccountOrName'));
      return;
    }

    // SWIFT requires BIC
    const missingBic = payments.filter(p => p.paymentType === 'swift' && !p.swiftBic);
    if (missingBic.length > 0) {
      toast.error(t('export.mbank.swiftRequiresBic'));
      return;
    }

    if (alreadyExported.length > 0 || alreadyPaid.length > 0) {
      setConfirmReExportOpen(true);
      return;
    }

    void performExport(false);
  }, [payments, alreadyExported, alreadyPaid, performExport, overrideDate, executionDate]);

  const handleConfirmReExport = useCallback(() => {
    setConfirmReExportOpen(false);
    void performExport(alreadyPaid.length > 0 || serverPaidWarning !== null);
  }, [performExport, alreadyPaid, serverPaidWarning]);

  // Totals grouped by currency
  const totalsByCurrency = useMemo(() => {
    const map = new Map<string, { count: number; amount: number; net: number; vat: number; types: Set<PaymentType> }>();
    for (const p of payments) {
      const cur = (p.currency || 'PLN').toUpperCase();
      const existing = map.get(cur) || { count: 0, amount: 0, net: 0, vat: 0, types: new Set<PaymentType>() };
      existing.count++;
      existing.amount += p.amount;
      existing.net += p.netAmount || 0;
      existing.vat += p.vatAmount || 0;
      existing.types.add(p.paymentType);
      map.set(cur, existing);
    }
    // Selected employee reimbursements ship in the same file, always in PLN —
    // fold them into the PLN total so the headline matches the real debit.
    const selectedReimbRecords = reimbRecords.filter((r) => selectedReimb.has(r.reimbursementId));
    if (selectedReimbRecords.length > 0) {
      const existing = map.get('PLN') || { count: 0, amount: 0, net: 0, vat: 0, types: new Set<PaymentType>() };
      existing.count += selectedReimbRecords.length;
      existing.amount += selectedReimbRecords.reduce((sum, r) => sum + r.amountPln, 0);
      existing.types.add('domestic');
      map.set('PLN', existing);
    }
    return map;
  }, [payments, reimbRecords, selectedReimb]);

  const paymentCount = payments.length + [...selectedReimb].filter((id) => reimbRecords.some((r) => r.reimbursementId === id)).length;

  const issueCount = useMemo(
    () => payments.filter(p => p.exportIssues.length > 0).length,
    [payments]
  );

  const canMerge = useMemo(() => {
    const nonMppPayments = payments.filter((p) => !p.hasSplitPayment && p.bankAccount);
    const keys = new Set(nonMppPayments.map((p) => `${p.bankAccount}::${(p.currency || 'PLN').toUpperCase()}::${p.paymentType}`));
    return nonMppPayments.length > keys.size;
  }, [payments]);

  /**
   * Export readiness WITH the reasons it is not ready. The button used to be
   * a bare disabled boolean, so an operator facing a blocked foreign transfer
   * ("no company account in EUR") had nothing to act on — the 2026-07 audit
   * of the FSS SWIFT invoices. The enable/disable rules are unchanged; only
   * `reasons` is new.
   */
  const canExport = useMemo(() => {
    const reasons: string[] = [];
    let ok = true;

    if (payments.length === 0 && selectedReimb.size === 0) {
      return { ok: false, reasons: [t('export.mbank.blockers.nothingToExport')] };
    }

    // Must have debtor account for each currency
    for (const [cur, acct] of currencyAccountMap) {
      if (!acct) {
        ok = false;
        reasons.push(t('export.mbank.blockers.noDebtorAccount', { currency: cur }));
      }
    }

    const noAccount = payments.filter(p => !p.bankAccount).length;
    const noName = payments.filter(p => !p.contractorName).length;
    if (noAccount > 0) {
      ok = false;
      reasons.push(t('export.mbank.blockers.noBankAccount', { count: noAccount }));
    }
    if (noName > 0) {
      ok = false;
      reasons.push(t('export.mbank.blockers.noContractorName', { count: noName }));
    }

    const mppIncomplete = payments
      .filter(p => p.hasSplitPayment)
      .filter(p => !(p.contractorNip && p.vatAmount && p.vatAmount > 0)).length;
    if (mppIncomplete > 0) {
      ok = false;
      reasons.push(t('export.mbank.blockers.mppIncomplete', { count: mppIncomplete }));
    }

    // SWIFT must have valid BIC
    const badBic = payments
      .filter(p => p.paymentType === 'swift')
      .filter(p => !p.swiftBic || !validateSwiftBic(p.swiftBic).valid).length;
    if (badBic > 0) {
      ok = false;
      reasons.push(t('export.mbank.blockers.swiftBic', { count: badBic }));
    }

    // International must have city+country
    const missingAddress = payments
      .filter(p => p.paymentType !== 'domestic')
      .filter(p => !p.contractorCity || !p.contractorCountry).length;
    if (missingAddress > 0) {
      ok = false;
      reasons.push(t('export.mbank.blockers.missingCityCountry', { count: missingAddress }));
    }

    if (!overrideDate) {
      const invalidDates = payments.filter(p => {
        if (p.paymentDate == null) return false;
        return !/^\d{4}-\d{2}-\d{2}$/.test(p.paymentDate) || !dayjs(p.paymentDate, 'YYYY-MM-DD', true).isValid();
      }).length;
      if (invalidDates > 0) {
        ok = false;
        reasons.push(t('export.mbank.blockers.invalidDate', { count: invalidDates }));
      }
    }

    // NOT a blocker: the backend normalizes the country on the way into the
    // XML and omits what it cannot map, so a vendor stored as "Canada" still
    // exports. Listed so the operator knows why the address will be partial.
    const unmappableCountry = payments
      .filter(p => p.exportIssues.includes('invalid_country_format')).length;
    if (unmappableCountry > 0) {
      reasons.push(t('export.mbank.blockers.invalidCountry', { count: unmappableCountry }));
    }

    return { ok, reasons };
  }, [payments, overrideDate, currencyAccountMap, selectedReimb]);

  const formatAmount = (value: number, currency = 'PLN') =>
    value.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + currency;

  const renderStatusCell = (record: PaymentRecord) => {
    const issues = record.exportIssues;
    if (!issues || issues.length === 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <CircleCheck className="inline-block size-4 text-[var(--color-success)]" />
          </TooltipTrigger>
          <TooltipContent>Ready for export</TooltipContent>
        </Tooltip>
      );
    }
    const labels = issues.map((i) => EXPORT_ISSUE_LABELS[i] || i).join(', ');
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <TriangleAlert className="inline-block size-4 text-[var(--color-warning)]" />
        </TooltipTrigger>
        <TooltipContent>{labels}</TooltipContent>
      </Tooltip>
    );
  };

  const renderPaymentDateCell = (record: PaymentRecord) => {
    if (!record.paymentDate) return <span className="text-muted-foreground">-</span>;
    const parsed = dayjs(record.paymentDate, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) return <span className="text-destructive">-</span>;
    return <span>{parsed.format('DD.MM.YYYY')}</span>;
  };

  const renderSwiftBicCell = (record: PaymentRecord) => {
    if (record.paymentType === 'domestic') {
      return <span className="text-muted-foreground">-</span>;
    }
    const isRequired = record.paymentType === 'swift';
    const hasError = isRequired && (!record.swiftBic || !validateSwiftBic(record.swiftBic).valid);
    return (
      <Input
        value={record.swiftBic || ''}
        onChange={(e) => handleSwiftBicChange(record.id, e.target.value)}
        placeholder={isRequired ? 'Required' : 'Optional'}
        maxLength={11}
        aria-invalid={hasError || undefined}
        className="h-7 font-mono text-[11px]"
      />
    );
  };

  const renderChargeBearerCell = (record: PaymentRecord) => {
    if (record.paymentType !== 'swift') {
      return (
        <span className="text-muted-foreground text-[11px]">
          {record.paymentType === 'sepa' ? 'SLEV' : '-'}
        </span>
      );
    }
    return (
      <Select
        value={record.chargeBearerCode}
        onValueChange={(v) => handleChargeBearerChange(record.id, v as ChargeBearerCode)}
      >
        <SelectTrigger size="sm" className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="SHAR">SHAR</SelectItem>
          <SelectItem value="DEBT">DEBT</SelectItem>
          <SelectItem value="CRED">CRED</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-10">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" aria-label="Go back" onClick={onClose}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg leading-tight font-semibold">mBank Export</h1>
            <p className="text-muted-foreground text-sm">{companyName}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isMerged ? (
            <Button variant="outline" onClick={handleUnmerge}>Unmerge payments</Button>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={handleMergePayments}
                    disabled={!canMerge}
                  >
                    <Merge className="size-4" />
                    Merge payments
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                {!canMerge
                  ? 'No payments can be merged (different accounts/currencies or all are MPP)'
                  : 'Merge payments to same bank account'}
              </TooltipContent>
            </Tooltip>
          )}
          {canExport.ok ? (
            <Button onClick={handleExport} disabled={exporting}>
              {exporting ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
              Export XML
            </Button>
          ) : (
            // A disabled button swallows pointer events — the span keeps the
            // tooltip (with the blocking reasons) reachable.
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button onClick={handleExport} disabled>
                    <Download className="size-4" />
                    Export XML
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <div className="flex flex-col gap-0.5">
                  {canExport.reasons.map((reason) => (
                    <span key={reason}>{reason}</span>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Export Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle>Export Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-x-12 gap-y-4">
            <div>
              <p className="text-muted-foreground mb-1 text-xs">
                Debtor Accounts (auto-matched by currency)
              </p>
              <div className="flex flex-col gap-1">
                {[...currencyAccountMap.entries()].map(([cur, acct]) => (
                  <div key={cur} className="flex items-center gap-2">
                    <Badge
                      className={
                        'min-w-10 justify-center ' +
                        (acct
                          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                          : 'border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-300')
                      }
                    >
                      {cur}
                    </Badge>
                    {acct ? (
                      <span className="font-mono text-xs">
                        {acct.name ? `${acct.name}: ` : ''}{acct.iban}
                      </span>
                    ) : (
                      <span className="text-destructive text-xs">
                        No matching account — add a {cur} account in company settings
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {(!company?.bankAccounts || company.bankAccounts.length === 0) && (
                <p className="text-destructive mt-1 text-xs">
                  No bank accounts configured for this company
                </p>
              )}
            </div>
            <div>
              <p className="text-muted-foreground mb-1 text-xs">
                Execution Date
              </p>
              <div className="flex items-center gap-3">
                <DatePicker
                  value={executionDate.toDate()}
                  onChange={(date) => date && setExecutionDate(dayjs(date))}
                  disabled={!overrideDate}
                  disabledDate={(current) => dayjs(current).isBefore(dayjs().startOf('day'))}
                  className="w-40"
                />
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="override-payment-dates"
                    checked={overrideDate}
                    onCheckedChange={(checked) => setOverrideDate(checked === true)}
                  />
                  <Label htmlFor="override-payment-dates" className="font-normal">
                    Override all payment dates
                  </Label>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Why the Export button is disabled (and what to double-check when it
          is not) — without this the operator only saw a dead button. */}
      {canExport.reasons.length > 0 && (
        <Alert className={ALERT_WARNING_CLASS}>
          <TriangleAlert className="size-4" />
          <AlertTitle>
            {canExport.ok
              ? t('export.mbank.blockers.warningsTitle')
              : t('export.mbank.blockers.title')}
          </AlertTitle>
          <AlertDescription>
            <ul className="list-inside list-disc">
              {canExport.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Already exported / already paid info */}
      {alreadyExported.length > 0 && (
        <Alert className={ALERT_INFO_CLASS}>
          <Info className="size-4" />
          <AlertTitle>
            {alreadyExported.length === 1
              ? '1 invoice was already exported'
              : `${alreadyExported.length} invoices were already exported`}
          </AlertTitle>
          <AlertDescription>
            <div>
              {alreadyExported.map((inv) => (
                <div key={inv.id}>
                  {inv.invoiceNumber || inv.id}
                  {inv.contractorName ? ` — ${inv.contractorName}` : ''}
                  {inv.exportedAt ? ` (exported ${dayjs(inv.exportedAt).format('DD.MM.YYYY')})` : ''}
                </div>
              ))}
              <div className="mt-1">
                You will be asked to confirm before the file is generated again.
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
      {alreadyPaid.length > 0 && (
        <Alert className={ALERT_WARNING_CLASS}>
          <TriangleAlert className="size-4" />
          <AlertTitle>
            {alreadyPaid.length === 1
              ? '1 invoice is already marked as paid'
              : `${alreadyPaid.length} invoices are already marked as paid`}
          </AlertTitle>
          <AlertDescription>
            <div>
              {alreadyPaid.map((inv) => (
                <div key={inv.id}>
                  {inv.invoiceNumber || inv.id}
                  {inv.contractorName ? ` — ${inv.contractorName}` : ''}
                </div>
              ))}
              <div className="mt-1">
                Exporting these again risks a double payment. You will be asked to confirm.
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      {/* MPP Warning */}
      {payments.some((p) => p.hasSplitPayment) && (
        <Alert className={ALERT_INFO_CLASS}>
          <Info className="size-4" />
          <AlertTitle>MPP invoices cannot be merged</AlertTitle>
          <AlertDescription>
            Invoices marked as MPP (Split Payment) will remain as separate payments. MPP is only available for domestic PLN transfers.
          </AlertDescription>
        </Alert>
      )}

      {/* Validation warnings */}
      {payments.some(p => p.hasSplitPayment && (!p.contractorNip || !p.vatAmount)) && (
        <Alert className={ALERT_WARNING_CLASS}>
          <TriangleAlert className="size-4" />
          <AlertTitle>MPP payments require additional data</AlertTitle>
          <AlertDescription>
            Split Payment (MPP) requires contractor NIP and VAT amount to be set.
          </AlertDescription>
        </Alert>
      )}

      {/* SWIFT BIC warning */}
      {payments.some(p => p.paymentType === 'swift' && !p.swiftBic) && (
        <Alert className={ALERT_WARNING_CLASS}>
          <TriangleAlert className="size-4" />
          <AlertTitle>SWIFT payments require SWIFT/BIC code</AlertTitle>
          <AlertDescription>
            Enter the beneficiary bank&apos;s SWIFT/BIC code for all SWIFT transfers.
          </AlertDescription>
        </Alert>
      )}

      {/* Export issues warning */}
      {issueCount > 0 && (
        <Alert className={ALERT_WARNING_CLASS}>
          <TriangleAlert className="size-4" />
          <AlertTitle>{`${issueCount} payment${issueCount > 1 ? 's have' : ' has'} export issues`}</AlertTitle>
          <AlertDescription>
            Check the warning icons in the table for details. Payments with issues may produce invalid XML.
          </AlertDescription>
        </Alert>
      )}

      {/* Payments Table */}
      <Card className="py-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">#</TableHead>
                <TableHead className="w-9 text-center" />
                <TableHead className="w-[65px]">Type</TableHead>
                <TableHead className="w-[60px]">MPP</TableHead>
                <TableHead>Contractor</TableHead>
                <TableHead className="w-40">Invoice number</TableHead>
                <TableHead className="w-[100px]">Payment date</TableHead>
                <TableHead className="w-[120px]">SWIFT/BIC</TableHead>
                <TableHead className="w-[85px]">Charges</TableHead>
                <TableHead className="w-[110px]">VAT Amount</TableHead>
                <TableHead className="w-[130px] text-right">Amount</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                    No data
                  </TableCell>
                </TableRow>
              ) : (
                payments.map((record, index) => {
                  const mppTooltip =
                    record.paymentType !== 'domestic'
                      ? 'MPP only for domestic PLN transfers'
                      : record.isMerged
                        ? 'Cannot change MPP for merged payments'
                        : '';
                  const mppSwitch = (
                    <Switch
                      checked={record.hasSplitPayment}
                      onCheckedChange={(checked) => handleMppToggle(record.id, checked)}
                      disabled={record.isMerged || record.paymentType !== 'domestic'}
                    />
                  );
                  return (
                    <TableRow
                      key={record.id}
                      className={
                        record.exportIssues.length > 0
                          ? 'bg-red-500/[0.06] hover:bg-red-500/10'
                          : record.isMerged
                            ? 'bg-[var(--color-merged-row-bg)] hover:bg-[var(--color-merged-row-hover)]'
                            : undefined
                      }
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell className="text-center">{renderStatusCell(record)}</TableCell>
                      <TableCell>
                        <Badge className={PAYMENT_TYPE_BADGE_CLASSES[record.paymentType]}>
                          {PAYMENT_TYPE_LABELS[record.paymentType]}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {mppTooltip ? (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-block">{mppSwitch}</span>
                            </TooltipTrigger>
                            <TooltipContent>{mppTooltip}</TooltipContent>
                          </Tooltip>
                        ) : (
                          mppSwitch
                        )}
                      </TableCell>
                      <TableCell className="max-w-[240px]">
                        <span className="flex items-center gap-1.5">
                          {record.vatAccountAssigned === false && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <TriangleAlert className="size-4 shrink-0 text-red-600 dark:text-red-500" />
                              </TooltipTrigger>
                              <TooltipContent>
                                {t('registry.whitelistWarning')} {t('registry.whitelistWarningDetail')}
                              </TooltipContent>
                            </Tooltip>
                          )}
                          <span className="truncate">{record.contractorName || '-'}</span>
                        </span>
                      </TableCell>
                      <TableCell className="max-w-40">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="block max-w-full truncate">{record.invoiceNumber}</span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {record.isMerged ? `Merged: ${record.invoiceNumber}` : record.invoiceNumber}
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{renderPaymentDateCell(record)}</TableCell>
                      <TableCell>{renderSwiftBicCell(record)}</TableCell>
                      <TableCell>{renderChargeBearerCell(record)}</TableCell>
                      <TableCell>
                        {record.hasSplitPayment ? (
                          <Input
                            type="number"
                            min={0.01}
                            step={0.01}
                            value={record.vatAmount ?? ''}
                            onChange={(e) => {
                              const parsed = e.target.value === '' ? null : Number(e.target.value);
                              handleVatAmountChange(
                                record.id,
                                parsed !== null && Number.isNaN(parsed) ? null : parsed,
                              );
                            }}
                            aria-invalid={!record.vatAmount || undefined}
                            className="h-7 text-xs"
                          />
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {record.amount.toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}{' '}
                        {record.currency || 'PLN'}
                      </TableCell>
                      <TableCell>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive hover:text-destructive size-7"
                              aria-label="Remove from export"
                              onClick={() => handleRemovePayment(record.id)}
                            >
                              <Trash2 className="size-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Remove from export</TooltipContent>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Approved employee reimbursements — same PAIN file, beneficiary = employee */}
      {reimbRecords.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-base">{t('export.mbank.reimbursements.title')}</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto pt-0">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>{t('export.mbank.reimbursements.employee')}</TableHead>
                  <TableHead>{t('export.mbank.reimbursements.account')}</TableHead>
                  <TableHead>{t('export.mbank.reimbursements.transferTitle')}</TableHead>
                  <TableHead className="text-right">{t('export.mbank.reimbursements.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reimbRecords.map((r) => (
                  <TableRow key={r.reimbursementId}>
                    <TableCell className="py-2">
                      <Checkbox
                        checked={selectedReimb.has(r.reimbursementId)}
                        onCheckedChange={(v) => {
                          setSelectedReimb((prev) => {
                            const next = new Set(prev);
                            if (v) next.add(r.reimbursementId);
                            else next.delete(r.reimbursementId);
                            return next;
                          });
                        }}
                      />
                    </TableCell>
                    <TableCell className="py-2 font-medium">{r.employeeName}</TableCell>
                    <TableCell className="py-2 font-mono text-xs">{r.iban}</TableCell>
                    <TableCell className="py-2">{r.title}</TableCell>
                    <TableCell className="py-2 text-right tabular-nums">
                      {formatAmount(r.amountPln, 'PLN')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Summary by currency */}
      <Card className="py-0">
        <CardContent className="px-4 py-3">
          <div className="flex flex-col gap-1">
            {[...totalsByCurrency.entries()].map(([cur, totals]) => {
              const typeLabel = [...totals.types].map(t => PAYMENT_TYPE_LABELS[t]).join('/');
              return (
                <div key={cur} className="flex items-center justify-between">
                  <span>
                    {typeLabel}: <span className="font-semibold">{totals.count}</span> payment{totals.count !== 1 ? 's' : ''}
                  </span>
                  <div className="flex items-center gap-4">
                    <span>
                      Net: <span className="font-semibold">{formatAmount(totals.net, cur)}</span>
                    </span>
                    <span className="text-muted-foreground">|</span>
                    <span>
                      VAT: <span className="font-semibold">{formatAmount(totals.vat, cur)}</span>
                    </span>
                    <span className="text-muted-foreground">|</span>
                    <span>
                      Total:{' '}
                      <span className="text-lg font-semibold">
                        {formatAmount(totals.amount, cur)}
                      </span>
                    </span>
                  </div>
                </div>
              );
            })}
            {totalsByCurrency.size > 1 && (
              <div className="flex items-center justify-between border-t pt-1">
                <span className="font-semibold">
                  Total: {paymentCount} payments across {totalsByCurrency.size} currencies
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Re-export confirmation */}
      <AlertDialog
        open={confirmReExportOpen}
        onOpenChange={(open) => {
          setConfirmReExportOpen(open);
          if (!open) setServerPaidWarning(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Export these invoices again?</AlertDialogTitle>
            <AlertDialogDescription>
              The payment file will include invoices that were already processed.
              Make sure the previous payment was not (or will not be) executed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="max-h-60 overflow-y-auto text-sm">
            {serverPaidWarning && (
              <p className="text-destructive mb-2 font-semibold">{serverPaidWarning}</p>
            )}
            {alreadyPaid.length > 0 && (
              <div className="mb-2">
                <p className="text-destructive font-semibold">
                  Already marked as paid — double payment risk:
                </p>
                {alreadyPaid.map((inv) => (
                  <div key={inv.id}>
                    {inv.invoiceNumber || inv.id}
                    {inv.contractorName ? ` — ${inv.contractorName}` : ''}
                  </div>
                ))}
              </div>
            )}
            {alreadyExported.length > 0 && (
              <div>
                <p className="font-semibold">Already exported:</p>
                {alreadyExported.map((inv) => (
                  <div key={inv.id}>
                    {inv.invoiceNumber || inv.id}
                    {inv.contractorName ? ` — ${inv.contractorName}` : ''}
                    {inv.exportedAt ? ` (${dayjs(inv.exportedAt).format('DD.MM.YYYY')})` : ''}
                  </div>
                ))}
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={alreadyPaid.length > 0 ? 'bg-destructive text-white hover:bg-destructive/90' : undefined}
              onClick={handleConfirmReExport}
            >
              Export anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
